import React, {useLayoutEffect, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {Helmet} from 'react-helmet'
/** @jsx jsx */
import {jsx, css, Global} from '@emotion/react'
import {
    Box,
    Flex,
    Heading,
    Skeleton,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import {PDCLink, PDCInnerAccordion} from '@app/page-designer/assets'
import {PDCAccordionBlock, PDCAccordion, PDCHomeHeroArticle} from '@app/page-designer/layouts'
import {CheckGiftcardForm} from './partials/check-giftcard-form'
import {BuyGiftCardForm} from './partials/buy-gift-card-form'
import {AddToCartModalGiftcard} from './partials/add-to-cart-modal'

// Constants
import {
    MAX_CACHE_AGE_STATIC_PAGES,
    STALE_WHILE_REVALIDATE,
    GIFTCARD_PDC_PAGE_ID,
    HOME_HREF
} from '@app/constants'

import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Utils
import {
    getEnvStaleTime,
    getPageDesignerRegion,
    getSizeOfElement,
    isHydrated
} from '@app/utils/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

// Hooks
import {useConst} from '@chakra-ui/react'
import {useConversionRate} from '@app/custom-contexts'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {usePage} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSiteType from '@app/hooks/use-site-type'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useGiftCardLogic} from '../../hooks/use-gift-card-logic'
import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PDCSubmenu} from '../../page-designer/assets'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcDisplayConfigLink': PDCLink,
    'commerce_assets.pdcInnerAccordion': PDCInnerAccordion,
    'commerce_assets.pdcSubmenu': PDCSubmenu,
    'commerce_layouts.pdcAccordionBlock': PDCAccordionBlock,
    'commerce_layouts.pdcAccordion': PDCAccordion,
    'commerce_layouts.pdcHomeHeroArticle': PDCHomeHeroArticle
}

/**
 * Component that renders the gift card page.
 * It has a hero banner, two forms: one for buying a gift card and one for checking the balance of a gift card,
 * a FAQ section. Shows a message if buying GCs is not available in the shopper's region, based on the active currency.
 */
const GiftCard = () => {
    const styles = useStyleConfig('Giftcard')
    const staleTime = getEnvStaleTime()
    const navigate = useNavigation()
    const conversionRate = useConversionRate()
    const modalDisclosure = useDisclosure()
    const contextValueGiftCards = useConst({components: PAGEDESIGNER_TO_COMPONENT})
    const {app: appConfig} = getConfig()
    const {shipToCountry} = useShipToCountry()
    const {isGCAvailableForCurrentCountry, isGCAvailableForCurrency} = useGiftCardLogic()

    const {data: basket, isFetchedAfterMount, refetch: refetchBasket} = useCurrentBasket()
    const {buildUrl} = useMultiSite()
    const {isOutlet} = useSiteType()
    const {currency: activeCurrency} = useCurrency()
    const {data: page, error} = usePage(
        {parameters: {pageId: GIFTCARD_PDC_PAGE_ID}},
        {staleTime: staleTime}
    )
    const {pageTitle, pageDescription, pageKeywords} = page || {}

    const [giftCardDetails, setGiftCardDetails] = useState({})

    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext()

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_STATIC_PAGES}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    if (error) {
        let ErrorClass = error.response?.status === 404 ? HTTPNotFound : HTTPError
        throw new ErrorClass(error.response?.statusText)
    }

    // Buying gift cards is only possible on the US and EU sites, excluding outlets.
    if (isOutlet) {
        navigate(buildUrl(HOME_HREF))
    }

    useLayoutEffect(() => {
        if (isOutlet) return
        refetchBasket()
    }, [])

    const subMenuComponent = getPageDesignerRegion(page, 'banner').components?.find(
        (comp) => comp?.typeId === 'commerce_assets.pdcSubmenu'
    )
    if (subMenuComponent) {
        subMenuComponent.currentPageId = GIFTCARD_PDC_PAGE_ID
    }
    const heroComponent = getPageDesignerRegion(page, 'banner').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcHomeHeroArticle'
    )
    const faqComponent = getPageDesignerRegion(page, 'main').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcAccordionBlock'
    )

    const HEIGHT_MULTIPLICATION_FACTOR = 1.5
    const scrollMarginTop =
        (isHydrated() ? getSizeOfElement('header').height : 0) * HEIGHT_MULTIPLICATION_FACTOR + 'px'

    // `isFetchedAfterMount` is used because we want to avoid stale data while changing shipping countries
    const isCurrencyDataLoaded =
        isFetchedAfterMount && conversionRate?.paymentCurrencyCode !== undefined
    const predefinedAmounts = appConfig?.GC_PREDEFINED_AMOUNTS
    const isCurrencySupported = isGCAvailableForCurrency(
        conversionRate?.paymentCurrencyCode || basket?.currency || activeCurrency
    )
    const isCountrySupported = isGCAvailableForCurrentCountry(shipToCountry.id)
    const isBuyingGiftCardsSupported = predefinedAmounts
        ? isCurrencySupported && isCountrySupported
        : false

    const onBuyGiftCardSuccess = (giftCardDetails) => {
        setGiftCardDetails({amount: giftCardDetails.amount})
        modalDisclosure.onOpen()
    }

    return (
        <Box layerStyle="page" css={[GiftCard.css]}>
            <Helmet>
                {pageTitle && <title>{pageTitle}</title>}
                {pageDescription && <meta name="description" content={pageDescription} />}
                {pageKeywords && <meta name="keywords" content={pageKeywords} />}
            </Helmet>
            <GlobalStyles />
            <PageContext.Provider value={contextValueGiftCards}>
                {subMenuComponent && <Component component={subMenuComponent} />}
                <Skeleton
                    isLoaded={isCurrencyDataLoaded}
                    height={isCurrencyDataLoaded ? 'auto' : '50vh'}
                >
                    {isBuyingGiftCardsSupported ? (
                        <>
                            {/* Hero */}
                            {heroComponent && <Component component={heroComponent} />}

                            {/* Forms */}
                            <Flex {...styles.formsContainer}>
                                <Box {...styles.form} id="buy" scrollMarginTop={scrollMarginTop}>
                                    <BuyGiftCardForm onSuccess={onBuyGiftCardSuccess} />
                                </Box>

                                <Box
                                    {...styles.form}
                                    id="balance"
                                    scrollMarginTop={scrollMarginTop}
                                >
                                    <CheckGiftcardForm />
                                </Box>
                            </Flex>
                        </>
                    ) : (
                        // Buying Gift Card is not available
                        <Box {...styles.gcNotAvailableContainer}>
                            <Text>
                                <FormattedMessage
                                    id="giftcard.buy_not_available"
                                    description="Shown on the gift card page when buying a gift card is not possible in the shopper's country."
                                    defaultMessage="Buying a gift card is not possible in your country."
                                />
                            </Text>
                        </Box>
                    )}
                </Skeleton>

                {/* FAQ */}
                {faqComponent && (
                    <Box id="faq" {...styles.faqContainer} scrollMarginTop={scrollMarginTop}>
                        <Heading size="4xl" textTransform="uppercase" mb="16">
                            <FormattedMessage id="giftcard.faq.title" defaultMessage="FAQ" />
                        </Heading>
                        <Component component={faqComponent} />
                    </Box>
                )}
            </PageContext.Provider>

            <AddToCartModalGiftcard giftCertificate={giftCardDetails} {...modalDisclosure} />
        </Box>
    )
}

GiftCard.css = css`
    .pdc-accordion-block-wrapper {
        padding: 0;
    }
`

GiftCard.displayName = 'GiftCard'

export default GiftCard

const GlobalStyles = () => (
    <Global
        styles={css`
            html {
                scroll-behavior: smooth;
            }
        `}
    />
)
